import { clean, getAbsoluteUrl, titleFromSlug } from './format';

// One promotion shape for both `view_promotion` and `select_promotion`, so an
// impression and the click that follows it carry identical identifiers and GA4
// can join them.

// `location_id` — the page section a creative sits in, in English, verbatim.
// `slotKey` — prefix of `creative_slot`, which is the key plus the creative's
// 1-based position inside the component (e.g. `hp_hero_slider_3`).
export const PROMOTION_SECTIONS = {
  heroSlider: { slotKey: 'hp_hero_slider', location: 'Homepage Hero Slider' },
  sideBanners: { slotKey: 'hp_side_banner', location: 'Homepage Side Banners' },
  promoTiles: { slotKey: 'hp_promo_tiles', location: 'Homepage Promo Tiles' },
  brandTiles: { slotKey: 'hp_brand_tiles', location: 'Homepage Brand Tiles' },
  brandDeals: { slotKey: 'hp_brand_deals', location: 'Homepage Brand Deals' },
  topBarTicker: { slotKey: 'top_bar_ticker', location: 'Top Bar Ticker' },
  plpBanner: { slotKey: 'plp_banner', location: 'PLP Banner' },
  plpSlider: { slotKey: 'plp_slider', location: 'PLP Banner' },
  categoriesPage: {
    slotKey: 'categories_banner',
    location: 'Categories Page Banners',
  },
};

export const getCreativeName = (image) => clean(image);

// Spec §2: every value goes out in English. The Arabic title is only used when
// the CMS has no English one, so the event still carries a name rather than
// dropping a required parameter.
export const getPromotionName = (info, keys = ['title', 'alt']) => {
  for (const key of keys) {
    const english = clean(info?.[`${key}-en`]);
    if (english) return english;
  }
  for (const key of keys) {
    const fallback = clean(info?.[`${key}-ar`]) || clean(info?.[key]);
    if (fallback) return fallback;
  }
  return undefined;
};

// A name for a banner the CMS sends without one (category-page banners carry
// only `image` and `url`), from where it leads: "b/babyjoy" -> "Babyjoy",
// "c/vitamins-supplements/sports-nutrition" -> "Sports Nutrition".
export const getNameFromLink = (link) =>
  titleFromSlug(
    String(link || '')
      .split(/[?#]/)[0]
      .split('/')
      .filter(Boolean)
      .at(-1)
  );

// The absolute URL the click actually navigates to, so pass the resolved route
// the link renders rather than the raw CMS value.
export const getPromotionDestination = (href, locale) =>
  getAbsoluteUrl(href, locale);

export function buildPromotion({
  section,
  position,
  id,
  name,
  image,
  itemId,
  itemName,
}) {
  const creative_slot = clean(
    position ? `${section?.slotKey}_${position}` : section?.slotKey
  );

  // The backend's own id when the CMS provides one, otherwise the slot
  // position. Both are stable and identical between AR and EN.
  const promotion_id = clean(id) || creative_slot;
  if (!promotion_id) return null;

  const promotion_name = clean(name);
  const creative_name = getCreativeName(image);
  const item_name = clean(itemName) || promotion_name;

  return {
    // GA4 requires item_id/item_name on every items entry. When the banner is
    // not promoting one specific product, the promotion stands in for it.
    item_id: clean(itemId) || promotion_id,
    ...(item_name && { item_name }),
    promotion_id,
    ...(promotion_name && { promotion_name }),
    ...(creative_name && { creative_name }),
    ...(creative_slot && { creative_slot }),
    ...(section?.location && { location_id: section.location }),
  };
}
